'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';

/**
 * Error boundary del dashboard (Next.js 16 usa la prop `retry`).
 * Muestra un fallback propio en lugar de la pantalla genérica de error.
 */
export default function DashboardError({ error, retry }) {
    useEffect(() => {
        // Dejamos rastro del error en consola para facilitar el diagnóstico.
        console.error('[Dashboard] Error de renderizado:', error);
    }, [error]);

    return (
        <div className="flex min-h-[70vh] w-full items-center justify-center px-4 py-10">
            <div
                role="alert"
                className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm"
            >
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-rose-100 bg-rose-50 text-rose-600">
                    <AlertTriangle size={26} aria-hidden="true" />
                </div>

                <h1 className="text-lg font-bold text-slate-900">Algo salió mal en este módulo</h1>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
                    No pudimos mostrar esta sección. Puedes reintentar la carga o volver al dashboard
                    para continuar trabajando en otro módulo.
                </p>

                {error?.digest && (
                    <p className="mt-4 text-[11px] text-slate-400">
                        Código de referencia:{' '}
                        <code className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">
                            {error.digest}
                        </code>
                    </p>
                )}

                <div className="mt-6 flex flex-col-reverse items-center justify-center gap-2 sm:flex-row">
                    <Link
                        href="/dashboard"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 sm:w-auto"
                    >
                        <ArrowLeft size={16} aria-hidden="true" />
                        Volver al dashboard
                    </Link>
                    <button
                        type="button"
                        onClick={() => retry()}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 sm:w-auto"
                    >
                        <RefreshCw size={16} aria-hidden="true" />
                        Reintentar
                    </button>
                </div>
            </div>
        </div>
    );
}
